import {
  BaitLaidSchema,
  BossBonusEventSchema,
  STREAK_PET_DAY,
  StreakRewardEventSchema,
  ContractClaimedSchema,
  ContractRejectedSchema,
  ContractsEventSchema,
  type ContractsEvent,
  getQuest,
  type NpcIdentity,
  QuestCompletedSchema,
  QuestRejectedSchema,
  type QuestMarker,
  QuestsEventSchema,
  type QuestsEvent,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { cue } from '../audio/cues.js';
import { actionKeyLabel } from '../settings/keybindings.js';
import { ContractBoard } from './contract-board.js';
import { CONTRACT_REFUSALS, contractRewardLine, contractTitle } from './contract-text.js';
import { type DialogNpc, QuestDialog } from './quest-dialog.js';
import { QuestJournal } from './quest-journal.js';
import { QuestLog } from './quest-log.js';
import { npcName, objectiveLine, QUEST_REFUSALS, rewardLine } from './quest-text.js';
import { QuestToast } from './quest-toast.js';
import { QuestTracker } from './quest-tracker.js';
import { uiFormat } from './text.js';

/**
 * Everything of the quests on the client (ADR 0047), for the world scene:
 * the journal the server sends, the dialogue with a townsperson, the
 * journal window, the tracker and the notices. The scene hands it the
 * messages and asks it, for each townsperson, which marker to draw and
 * whether a click opens the dialogue. It only ever sends intents.
 */

export interface QuestClientOptions {
  send: (type: string, payload: unknown) => void;
  notice: (text: string) => void;
  /** Why an action did not happen: over the world, not in the chat. */
  refuse: (text: string) => void;
  icons: () => CssAtlas | null;
}

export class QuestClient {
  readonly log = new QuestLog();
  private readonly dialog: QuestDialog;
  private readonly journal: QuestJournal;
  private readonly tracker: QuestTracker;
  private readonly toast = new QuestToast();
  private readonly contracts: ContractBoard;
  /** The contracts before the last update, to tell one just filled; undefined until the first. */
  private previousContracts: ContractsEvent | undefined;
  /** The journal before the last update, to tell what moved; undefined until the first. */
  private previous: QuestsEvent | undefined;

  constructor(private readonly options: QuestClientOptions) {
    this.dialog = new QuestDialog({
      log: this.log,
      icons: options.icons,
      onAccept: (questId, npcId) => {
        options.send('quest_accept', { questId, npcId });
      },
      onTurnIn: (questId, npcId) => {
        options.send('quest_turn_in', { questId, npcId });
      },
    });
    this.contracts = new ContractBoard({
      icons: options.icons,
      onClaim: (contractId) => {
        options.send('contract_claim', { contractId });
      },
    });
    this.journal = new QuestJournal({
      log: this.log,
      icons: options.icons,
      contracts: this.contracts,
      onAbandon: (questId) => {
        options.send('quest_abandon', { questId });
      },
    });
    this.tracker = new QuestTracker({
      log: this.log,
      onOpen: (questId) => {
        this.journal.open(questId);
      },
    });
  }

  setLevel(level: number): void {
    this.log.setLevel(level);
  }

  markerOf(npc: NpcIdentity): QuestMarker | null {
    return this.log.markerOf(npc);
  }

  /** The townsperson whose dialogue is open, if any. */
  get dialogNpcId(): string | undefined {
    return this.dialog.npcId;
  }

  closeDialog(): void {
    this.dialog.close();
  }

  /**
   * A click reached a townsperson. A keeper of the quests always answers; a
   * keeper with a trade only when he has something for a quest, his own
   * window a button away. False leaves the scene to open that window itself.
   */
  visit(npc: DialogNpc, questKeeper: boolean): boolean {
    const agenda = this.log.agendaOf(npc);
    if (!questKeeper && !this.log.hasAgenda(npc)) return false;
    // His words for a quest under way are had as soon as he is spoken to.
    if (agenda.talks.length > 0) this.options.send('quest_talk', { npcId: npc.id });
    this.dialog.open(npc);
    cue('sfx.open');
    return true;
  }

  onQuests(payload: unknown): void {
    const parsed = QuestsEventSchema.safeParse(payload);
    if (!parsed.success) return;
    const event = parsed.data;
    if (this.previous !== undefined) this.announce(this.previous, event);
    this.previous = event;
    this.log.update(event);
  }

  onCompleted(payload: unknown): void {
    const parsed = QuestCompletedSchema.safeParse(payload);
    const quest = parsed.success ? getQuest(parsed.data.questId) : undefined;
    if (!parsed.success || quest === undefined) return;
    const paid = rewardLine(quest, parsed.data.xp);
    this.toast.show({
      kicker: 'Quête accomplie',
      name: quest.name,
      detail: paid,
      icon: 'quest-ready',
    });
    this.options.notice(
      uiFormat('Quête accomplie : {quest}. {reward}', { quest: quest.name, reward: paid }),
    );
    cue('sfx.upgrade-success');
  }

  /** The day's and the week's contracts (ADR 0051): a contract just filled is told once. */
  onContracts(payload: unknown): void {
    const parsed = ContractsEventSchema.safeParse(payload);
    if (!parsed.success) return;
    const before = this.previousContracts;
    this.previousContracts = parsed.data;
    this.contracts.update(parsed.data);
    this.journal.refreshTabs();
    if (before === undefined) return;
    const was = new Map([...before.daily, before.weekly].map((c) => [c.id, c]));
    for (const contract of [...parsed.data.daily, parsed.data.weekly]) {
      const old = was.get(contract.id);
      const filled = contract.progress >= contract.count;
      if (!filled || contract.claimed || old === undefined || old.progress >= old.count) continue;
      if (old.kind !== contract.kind) continue;
      this.toast.show({
        kicker: 'Contrat rempli',
        name: contractTitle(contract),
        detail: uiFormat('Encaisse-le dans le journal ({key}), onglet Contrats.', {
          key: actionKeyLabel('quests'),
        }),
        icon: 'quest-ready',
      });
      this.options.notice(
        uiFormat('Contrat rempli : {contract}.', { contract: contractTitle(contract) }),
      );
      cue('sfx.discover');
    }
  }

  /** Whether this boss's first kill today still pays this player a second loot. */
  bossBonusAvailable(bossId: string): boolean {
    return this.previousContracts?.bossBonus.includes(bossId) ?? false;
  }

  /** The day's first kill of a boss: its second loot lies by the corpse. */
  onBossBonus(payload: unknown): void {
    const parsed = BossBonusEventSchema.safeParse(payload);
    if (!parsed.success) return;
    const name = npcName(parsed.data.bossId);
    this.toast.show({
      kicker: 'Butin du jour',
      name,
      detail: 'Ton butin t’attend près de lui.',
      icon: 'quest-ready',
    });
    this.options.notice(uiFormat('{boss} : ton butin du jour est tombé !', { boss: name }));
    cue('sfx.loot-rare');
  }

  /** A day counted in the streak, and what it paid; the seventh brings the fox. */
  onStreakReward(payload: unknown): void {
    const parsed = StreakRewardEventSchema.safeParse(payload);
    if (!parsed.success) return;
    const { days } = parsed.data;
    const paid = contractRewardLine(parsed.data);
    const fox = days === STREAK_PET_DAY;
    this.toast.show({
      kicker: uiFormat('Série : jour {days}', { days }),
      name: fox ? 'Renard de lumière' : 'Récompense de série',
      detail: fox ? 'Il t’attend dans ton sac : équipe-le comme familier.' : paid,
      icon: 'quest-ready',
    });
    this.options.notice(
      uiFormat('Série de connexions, jour {days} : {reward}.', { days, reward: paid }),
    );
    cue(fox ? 'sfx.level-up' : 'sfx.coins');
  }

  onContractClaimed(payload: unknown): void {
    const parsed = ContractClaimedSchema.safeParse(payload);
    if (!parsed.success) return;
    this.options.notice(
      uiFormat('Contrat encaissé : {reward}.', { reward: contractRewardLine(parsed.data) }),
    );
    cue('sfx.coins');
  }

  onContractRejected(payload: unknown): void {
    const parsed = ContractRejectedSchema.safeParse(payload);
    if (!parsed.success) return;
    this.options.refuse(CONTRACT_REFUSALS[parsed.data.reason]);
    cue('sfx.error');
    // The button was greyed on click: the board draws it again as it stands.
    if (this.previousContracts !== undefined) this.contracts.update(this.previousContracts);
  }

  /** A bait laid (ADR 0047): its catch is out of the den, for this player and their party. */
  onBaitLaid(payload: unknown): void {
    const parsed = BaitLaidSchema.safeParse(payload);
    if (!parsed.success) return;
    this.options.notice(
      uiFormat('Un {mob} sort du terrier, attiré par l’odeur !', {
        mob: npcName(parsed.data.mobId),
      }),
    );
    cue('sfx.boss-roar', 0.5);
  }

  onRejected(payload: unknown): void {
    const parsed = QuestRejectedSchema.safeParse(payload);
    if (!parsed.success) return;
    if (this.dialog.isOpen) this.dialog.refused(parsed.data.reason);
    else this.options.refuse(QUEST_REFUSALS[parsed.data.reason]);
    cue('sfx.error');
  }

  /** A quest taken, an objective met, a quest finished: said in the chat and on the plate. */
  private announce(before: QuestsEvent, after: QuestsEvent): void {
    const was = new Map(before.active.map((quest) => [quest.id, quest]));
    for (const now of after.active) {
      const quest = getQuest(now.id);
      if (quest === undefined) continue;
      const old = was.get(now.id);
      if (old === undefined) {
        this.toast.show({
          kicker: 'Nouvelle quête',
          name: quest.name,
          detail: quest.summary,
          icon: 'quest-available',
        });
        this.options.notice(uiFormat('Nouvelle quête : {quest}.', { quest: quest.name }));
        cue('sfx.discover');
        continue;
      }
      const met = (progress: { value: number; target: number } | undefined): boolean =>
        progress !== undefined && progress.value >= progress.target;
      const allNow = now.progress.every(met);
      if (allNow && !old.progress.every(met)) {
        this.toast.show({
          kicker: 'Quête terminée',
          name: quest.name,
          detail: uiFormat('Retourne voir {npc}.', { npc: npcName(quest.turnIn.mob) }),
          icon: 'quest-ready',
        });
        cue('sfx.discover');
        continue;
      }
      now.progress.forEach((progress, index) => {
        if (met(progress) && !met(old.progress[index])) {
          this.options.notice(
            uiFormat('Objectif accompli : {objective}.', {
              objective: objectiveLine(quest, index, progress),
            }),
          );
        }
      });
    }
  }

  destroy(): void {
    this.dialog.destroy();
    this.journal.destroy();
    this.contracts.destroy();
    this.tracker.destroy();
    this.toast.destroy();
  }
}
